/**
 * This file defines the AddPortfolioDialog component, responsible for rendering
 * and handling the functionality of a dialog that allows users to add a portfolio.
 * It uses several custom hooks for form handling and user data, as well as
 * components for input fields and form container.
 *
 */
import PropTypes from 'prop-types';
import * as React from 'react';
import { useUser, useUserFields } from '../../hooks/user';
import { DialogInput } from './DialogInput';
import { useForm } from '../../hooks/form';
import { FormContainer } from './FormContainer';
import PhoneNumberInput from './PhoneNumberInput';
import {
    getUserActionSubmitConfig,
    validatePhoneNumber
} from '../../functions/forms';
import { UserActionType } from '../menus/menuItemsConfig';
import { Stack } from '@mui/system';

const requiredFields = ['portfolioName'];

const validationConfig = {
    phoneNumber: {
        validate: validatePhoneNumber,
        additionalValidationArguments: ['countryCode']
    }
};

/**
 * Component for adding a new portfolio via a dialog interface. It renders input
 * fields for user information and portfolio details, validates input, and handles
 * form submission.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Indicates if the dialog is open.
 * @param {Function} props.handleFormClose - Function to close the Add Portfolio dialog.
 * @param {Function} props.handleSubmissionViewClose - Function called when Submission View closes
 * @returns {React.ReactElement} The rendered AddPortfolioDialog component
 */
const AddPortfolioDialog = ({
    open,
    handleFormClose,
    handleSubmissionViewClose
}) => {
    const formId = UserActionType.ADD_PORTFOLIO;

    const { getFirstName, getLastName, getEmail } = useUserFields();
    const { user } = useUser();

    const initialFormState = {
        fullName: { value: `${getFirstName()} ${getLastName()}` },
        phoneNumber: {
            countryCode: '1',
            value: '+1',
            error: false,
            valid: true
        },
        portfolioName: { value: '', error: false, clicked: false },
        symbol: { value: '' },
        comments: { value: '' }
    };

    const submitConfig = getUserActionSubmitConfig(formId, user);

    const {
        formState,
        resetFields,
        setFormField,
        handleSend,
        setFormFieldClicked,
        submissionState,
        isErrorAndClicked,
        setPhoneNumber
    } = useForm(
        initialFormState,
        requiredFields,
        validationConfig,
        submitConfig
    );

    /**
     * Constructs the form fields using DialogInput and PhoneNumberInput components.
     */
    const formFields = (
        <Stack spacing={2.5} padding={2}>
            <DialogInput
                id='firstName'
                label='First name'
                value={getFirstName()}
                disabled
                data-analyticsid='first-name'
            />
            <DialogInput
                id='lastName'
                label='Last name'
                value={getLastName()}
                disabled
                data-analyticsid='last-name'
            />
            <DialogInput
                id='emailTextField'
                label='Email'
                value={getEmail()}
                disabled
                data-analyticsid='email'
            />
            <PhoneNumberInput
                value={formState.phoneNumber.value}
                onChange={setPhoneNumber}
                error={formState.phoneNumber.error}
                valid={formState.phoneNumber.valid}
                helperText='We will only use this number to call you about this request'
                placeholder='Phone number'
                data-analyticsid='phone-number'
            />
            <DialogInput
                id={'portfolioName'}
                label='Portfolio name'
                value={formState.portfolioName.value}
                onChange={(e) => setFormField('portfolioName', e.target.value)}
                required
                helperText={
                    isErrorAndClicked('portfolioName')
                        ? 'This field is mandatory'
                        : ' '
                }
                error={isErrorAndClicked('portfolioName')}
                onBlur={() => setFormFieldClicked('portfolioName')}
                data-analyticsid='portfolio-name'
            />
            <DialogInput
                id={'symbol'}
                style={{ marginTop: '5px' }} //manual Stack spacing override to account for helperText above
                value={formState.symbol.value}
                placeholder='Morningstar ticker, ISIN, or other symbol'
                onChange={(e) => setFormField('symbol', e.target.value)}
                data-analyticsid='ticker'
            />
            <DialogInput
                id={'comments'}
                value={formState.comments.value}
                placeholder='Any comments?'
                onChange={(e) => setFormField('comments', e.target.value)}
                multiline
                rows={4}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '20px'
                    }
                }}
                data-analyticsid='comments'
            />
        </Stack>
    );

    return (
        <FormContainer
            formId={formId}
            formState={formState}
            open={open}
            handleClose={handleFormClose}
            title={'Add a Portfolio'}
            submissionView={"Thank you!\nWe'll be in touch soon"}
            onSubmissionViewClose={handleSubmissionViewClose}
            requiredFields={requiredFields}
            handleSend={handleSend}
            submissionState={submissionState}
            resetFields={resetFields}
            dataAnalyticsId='add-portfolio-form'
        >
            {formFields}
        </FormContainer>
    );
};

AddPortfolioDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleFormClose: PropTypes.func.isRequired,
    handleSubmissionViewClose: PropTypes.func
};
export default AddPortfolioDialog;
